import { Alert, Button, Divider, Modal } from 'antd';
import AccountSection from './AccountSection';
import { useDisclosure } from '@/hooks';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { getUserFromLocalStorage } from '@/utils/user';
import moment from 'moment';
import { Group } from '@/components/_common';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import StatusTag from '@/components/_common/StatusTag';

interface DataCommand {
  title: ReactNode;
  description: ReactNode;
  command?: string;
  data?: DataCommand[];
}

const data: DataCommand[] = [
  {
    title: 'Invocação da Ultracar Skill',
    command: 'Alexa, sistema Ultracar',
    description: (
      <>
        <p>
          Ao informar este comando para o dispositivo da Alexa pela primeira
          vez, ela irá verificar se tem conta vinculada e retorna uma mensagem
          padrão de saudação.
          <br />
          Caso contrário, ela pedirá para que faça a vinculação de conta. Após a
          saudação, você estará autenticado e poderá fazer qualquer uma das
          perguntas abaixo.
        </p>
        <p>
          <strong>Exemplos:</strong>
          <br />
          <strong>- Invocação + Pergunta:</strong>{' '}
          <code>Alexa, sistema Ultracar</code>; Após a saudação,{' '}
          <code>Qual o total a receber no mês?</code>
          <br />
          <strong>- Pergunta direta:</strong>{' '}
          <code>
            Alexa, pergunte ao sistema Ultracar qual o total a receber no mês
          </code>
        </p>
        <Alert
          type="warning"
          description={
            <>
              <strong>Importante</strong>
              <p>
                Para ter acessos as funcionalidades da Ultracar Skill, é preciso
                ter as respectivas permissões habilitadas no grupo de perfil,
                caso contrário, a Alexa irá retornar uma mensagem informando que
                não há permissão.
              </p>
              <Group gap={8}>
                <StatusTag
                  color="green"
                  label={
                    <Group gap={6}>
                      <FaCheckCircle />
                      Há permissão
                    </Group>
                  }
                />
                <StatusTag
                  color="red"
                  label={
                    <Group gap={6}>
                      <FaTimesCircle />
                      Sem permissão
                    </Group>
                  }
                />
              </Group>
            </>
          }
        />
      </>
    ),
  },
  {
    title: 'Funcionalidades de indicadores da dashboard',
    command: 'Alexa, Qual o total de orçamentos abertos no mês?',
    description: (
      <>
        <p>
          Esta funcionalidade retorna dados de indicadores das suas oficinas,
          também disponíveis no dashboard do sistema.
        </p>

        <p>
          <strong>Lista de perguntas dos indicadores da dashboard</strong>
          <ul style={{ paddingLeft: '1rem' }}>
            <li>
              contas a receber no mês{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_MANAGEMENT]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              contas a pagar no mês{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_MANAGEMENT]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              ticket médio no mês{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_BUDGET]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              total do ticket médio{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_BUDGET]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              entregas no mês{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_BUDGET]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              entregas atrasadas{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_BUDGET]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              orçamentos abertos no mês{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_MANAGEMENT]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              orçamentos autorizados no mês{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_MANAGEMENT]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              entradas de veículos no mês{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_MANAGEMENT]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              saídas de veículos no mês{' '}
              {hasPermissions([Authorities.DASHBOARD_VIEW_MANAGEMENT]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
          </ul>
        </p>
        <p>Em breve novos indicadores...</p>
      </>
    ),
  },

  {
    title: 'Funcionalidade de consulta de ticket médio de peças e serviços',
    command: 'Alexa, Qual o ticket médio de peças e serviços?',
    description: (
      <>
        <p>
          Está funcionalidade retorna os valores de ticket médio das peças e
          serviços.
          <br />
          Se você não informar um período, a Alexa irá considerar o mês atual
          para consulta.
          <br />
          Para consultar um período específico é necessário informar o mês e o
          ano de referência, caso não informe o ano a Alexa irá considerar o mês
          futuro.
        </p>
        <p>
          <strong>Exemplos:</strong>
          <ul style={{ paddingLeft: '1rem' }}>
            <li>
              ticket médio de peças e serviços{' '}
              {hasPermissions([Authorities.DEFAULT_MANAGERIAL_GRAPHICS]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              {'ticket médio de peças e serviços em {Período}'}{' '}
              {hasPermissions([Authorities.DEFAULT_MANAGERIAL_GRAPHICS]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              {'ticket médio de peças e serviços no mês de {Período}'}
              {hasPermissions([Authorities.DEFAULT_MANAGERIAL_GRAPHICS]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
          </ul>
        </p>

        <p>
          <strong>Exemplos com períodos:</strong>
          <ul style={{ paddingLeft: '1rem' }}>
            <li>
              ticket médio de peças e serviços em{' '}
              <code>{moment().format('MMMM')}</code> (irá buscar o mês atual)
            </li>
            <li>
              ticket médio de peças e serviços em{' '}
              <code>{moment().subtract(1, 'month').format('MMMM')}</code>{' '}
              {`(irá buscar o mês de ${moment()
                .subtract(1, 'month')
                .format('MMMM')} de 2025)`}
            </li>
            <li>
              ticket médio de peças e serviços no mês de{' '}
              <code>
                {moment().subtract(1, 'month').format('MMMM [de] YYYY')}
              </code>{' '}
              {`(irá buscar o mês de ${moment()
                .subtract(1, 'month')
                .format('MMMM [de] YYYY')})`}
            </li>
          </ul>
        </p>
        <p>
          <strong>Porque preciso informar o ano além do mês?</strong>
          <br />
          Informando o ano, você consegue consultar dados de períodos passado,
          de um ou dois anos atrás.
        </p>
      </>
    ),
  },
  {
    title: 'Funcionalidade de consulta de faturamento',
    command: 'Alexa, Qual o meu faturamento?',
    description: (
      <>
        <p>
          Esta funcionalidade irá retornar o valor todal do seu faturamento do
          mês atual, ou para um período específico
          <br />
          Se você não informar um período, a Alexa irá considerar o mês atual
          para consulta.
          <br />
          Para consultar um período específico é necessário informar o mês e o
          ano de referência, caso não informe o ano a Alexa irá considerar o mês
          futuro.
        </p>
        <p>
          <strong>Exemplos:</strong>
          <ul style={{ paddingLeft: '1rem' }}>
            <li>
              {'meu faturamento'}{' '}
              {hasPermissions([Authorities.DEFAULT_MANAGERIAL_GRAPHICS]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              {'meu faturamento em {Período}'}{' '}
              {hasPermissions([Authorities.DEFAULT_MANAGERIAL_GRAPHICS]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              {'meu faturamento no mês de {Período}'}{' '}
              {hasPermissions([Authorities.DEFAULT_MANAGERIAL_GRAPHICS]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              {'faturamento em {Período}'}{' '}
              {hasPermissions([Authorities.DEFAULT_MANAGERIAL_GRAPHICS]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              {'faturamento no mês de {Período}'}{' '}
              {hasPermissions([Authorities.DEFAULT_MANAGERIAL_GRAPHICS]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
          </ul>
        </p>

        <p>
          <strong>Exemplos com períodos:</strong>
          <ul style={{ paddingLeft: '1rem' }}>
            <li>
              meu faturamento em <code>{moment().format('MMMM')}</code> (irá
              buscar o mês atual)
            </li>
            <li>
              faturamento em{' '}
              <code>{moment().subtract(1, 'month').format('MMMM')}</code>{' '}
              {`(irá buscar o mês de ${moment()
                .subtract(1, 'month')
                .format('MMMM')} de 2025)`}
            </li>
            <li>
              faturamento no mês de{' '}
              <code>
                {moment().subtract(1, 'month').format('MMMM [de] YYYY')}
              </code>{' '}
              {`(irá buscar o mês de ${moment()
                .subtract(1, 'month')
                .format('MMMM [de] YYYY')})`}
            </li>
          </ul>
        </p>

        <p>
          <strong>Porque preciso informar o ano além do mês?</strong>
          <br />
          Informando o ano, você consegue consultar dados de períodos passado,
          de um ou dois anos atrás.
        </p>
      </>
    ),
  },
  {
    title: 'Funcionalidade de consulta de preço de venda da peça',
    command:
      'Alexa, Qual o preço de venda da peça {SKU} para empresa {NomeFantasia}?',
    description: (
      <>
        <p>
          Esta funcionalidade retorna qual o preço de venda de uma peça, com
          base nos parâmetros pré definido para uma empresa especifica.
          <br />
          Onde <code>{`{SKU}`}</code> é o código interno da peça e{' '}
          <code>{`{NomeFantasia}`}</code> é o nome fantasia da empresa vinculada
          ao seu usuário,{' '}
          <Link
            to={`/app/registry/employees/${
              getUserFromLocalStorage().employeeId
            }/personal`}
            style={{ fontWeight: 600 }}
          >
            ver detalhe do funcionário
          </Link>
        </p>
        <p>
          <strong>Exemplos:</strong>
          <ul style={{ paddingLeft: '1rem' }}>
            <li>
              {`preço de venda da peça {SKU} para empresa {NomeFantasia}`}{' '}
              {hasPermissions([Authorities.REGISTER_PART_VIEW]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </li>
            <li>
              {`Qual o preço de venda da peça {SKU}?`}{' '}
              {hasPermissions([Authorities.REGISTER_PART_VIEW]) ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
              <ul style={{ paddingLeft: '1rem' }}>
                <li>
                  Se o seu usuário só tem vínculo a uma empresa, a Alexa irá
                  retornar o preço de venda dessa empresa.
                </li>
                <li>
                  Se o seu usuário tiver vínculo com mais de uma empresa, a
                  Alexa perguntará qual empresa gostaria de saber o preço de
                  venda e informará todas as empresas que você tem vínculo.
                  <br />
                  Porém, você pode interrompe-la e informar qual a empresa,
                  dizendo: <code>{`Alexa, empresa {NomeFantasia}`}</code>
                </li>
              </ul>
            </li>
          </ul>
        </p>
      </>
    ),
  },
];

export default function AlexaInfo() {
  const [modalOpened, modalAction] = useDisclosure();

  const renderDataItem = (
    dataItem: DataCommand,
    index: number,
    arr: DataCommand[],
  ) => {
    return (
      <div key={index} className="markdown">
        <strong style={{ fontSize: '1.15rem', lineHeight: 1 }}>
          {dataItem.title}
        </strong>
        {!!dataItem.command && (
          <div style={{ padding: '8px 0px' }}>
            <code>{dataItem.command}</code>
          </div>
        )}
        <p style={{ whiteSpace: 'pre-wrap' }}>{dataItem.description}</p>
        {!!dataItem.data?.length && dataItem.data.map(renderDataItem)}

        {arr.length - 1 > index && <Divider />}
      </div>
    );
  };

  return (
    <div>
      <AccountSection
        action={<Button onClick={modalAction.open}>Saiba mais</Button>}
        section={{
          title: 'Alexa',
          icon: (
            <img
              src="/images/alexa-logo.png"
              alt="Logo Alexa"
              width={60}
              style={{ padding: 6 }}
            />
          ),
          description: (
            <div>
              Tenha acesso a comandos para automatizar o dia a dia da sua
              oficina, tais como: 'Valor a pagar no mês', 'Total de entregas
              atrasadas', entre outros.
            </div>
          ),
        }}
      />

      <Modal
        width={800}
        open={modalOpened}
        onCancel={modalAction.close}
        title="Sobre as funcionalidades da Ultracar Skill"
        okButtonProps={{ style: { display: 'none' } }}
        cancelText="Fechar"
      >
        {data.map(renderDataItem)}
      </Modal>
    </div>
  );
}
